import React, { useState } from 'react';
import { AgentItem } from '../../domain/types';
import { useZunelo } from '../../context/ZuneloContext';
import { BrandIcon, Badge, Pill, Button, Accordion, CardExpandToggle } from '../../ui/common';

interface AgentCardProps {
  agent: AgentItem;
  isSelected?: boolean;
}

export const AgentCard: React.FC<AgentCardProps> = ({ agent: agentProp, isSelected = false }) => {
  const { selectItem, toggleAgentRunning, launchAgentTask, isItemExpanded, toggleItemExpanded, getAgentByName } = useZunelo();

  // Prefer the live agent from context (kept in sync with mutations) and fall
  // back to the prop for callers that render a card in isolation.
  const agent = getAgentByName(agentProp.name) || agentProp;

  const isExpanded = isItemExpanded(agent.name);

  const [customTaskPrompt, setCustomTaskPrompt] = useState<string>('');
  const [isLaunching, setIsLaunching] = useState<boolean>(false);

  const handleToggleRunning = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleAgentRunning(agent.name);
  };

  const handleLaunchTask = async (promptText: string, e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.stopPropagation();
    const task = promptText.trim();
    if (!task) return;

    setIsLaunching(true);
    await launchAgentTask(agent.name, task);
    setCustomTaskPrompt('');
    setIsLaunching(false);
  };

  const presetTasks = [
    'Ejecutar suite de tests',
    'Refactorizar imports y tipos',
    'Validar cobertura en staging',
  ];

  return (
    <article
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: '15px 18px',
        borderRadius: 18,
        cursor: 'pointer',
        overflow: 'hidden',
        background: isSelected ? 'var(--z-accent-wash)' : 'var(--z-surface)',
        border: 'none',
        boxShadow: isSelected ? '0 4px 16px var(--z-sh-6)' : '0 1px 2px var(--z-sh-4)',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Accent strip */}
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: agent.running ? 'var(--z-ok)' : 'var(--z-warn)',
        }}
      />

      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={() => {
          selectItem(agent.name);
          toggleItemExpanded(agent.name);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectItem(agent.name);
            toggleItemExpanded(agent.name);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}>
          <BrandIcon source="agents" size={15} />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--z-muted-2)',
            }}
          >
            {agent.name}
          </span>
          <Badge label={agent.ticket} tone="accent" />
          <Pill label={agent.state} tone={agent.running ? 'ok' : 'warn'} />
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--z-dim)' }}>
            {agent.elapsed}
          </span>
          <CardExpandToggle
            isExpanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              toggleItemExpanded(agent.name);
              selectItem(agent.name);
            }}
            ariaLabel={isExpanded ? 'Contraer agente' : 'Expandir agente'}
          />
        </div>

        <h3
          style={{
            margin: '0 0 9px',
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: '-0.015em',
            color: 'var(--z-text-2)',
            textWrap: 'pretty',
          }}
        >
          {agent.last}
        </h3>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--z-dim)' }}>
            Rama {agent.branch} · {agent.tokens} · {agent.cost}
          </span>
        </div>
      </div>

      {/* Expanded Metrics Box & Interactive Task Launcher */}
      {isExpanded && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: '12px 13px',
            marginBottom: 12,
            borderRadius: 12,
            background: 'var(--z-fill)',
            border: 'none',
          }}
        >
          {/* Stats Bar */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: '1 1 0', padding: '9px 10px', borderRadius: 10, background: 'var(--z-plate)', border: 'none' }}>
              <div style={{ fontSize: 10, color: 'var(--z-faint)', marginBottom: 2 }}>Rama</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: 'var(--z-text-2)' }}>
                {agent.branch}
              </div>
            </div>
            <div style={{ flex: '1 1 0', padding: '9px 10px', borderRadius: 10, background: 'var(--z-plate)', border: 'none' }}>
              <div style={{ fontSize: 10, color: 'var(--z-faint)', marginBottom: 2 }}>Tokens consumidos</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: 'var(--z-accent-3)' }}>
                {agent.tokens}
              </div>
            </div>
            <div style={{ flex: '1 1 0', padding: '9px 10px', borderRadius: 10, background: 'var(--z-plate)', border: 'none' }}>
              <div style={{ fontSize: 10, color: 'var(--z-faint)', marginBottom: 2 }}>Coste estimado</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: 'var(--z-text-2)' }}>
                {agent.cost}
              </div>
            </div>
          </div>

          {/* Interactive Task Launcher Simulation */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: '11px 12px',
              borderRadius: 10,
              background: 'var(--z-plate)',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
                Lanzar nueva tarea al agente:
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--z-accent-3)', fontWeight: 600 }}>
                ✦ Ejecución en vivo
              </span>
            </div>

            {/* Quick Task Presets */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {presetTasks.map((pt) => (
                <button
                  key={pt}
                  onClick={(e) => handleLaunchTask(pt, e)}
                  disabled={isLaunching}
                  style={{
                    padding: '4px 9px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: 'var(--z-fill-2)',
                    color: 'var(--z-accent-ink)',
                    border: 'none',
                  }}
                >
                  ▶ {pt}
                </button>
              ))}
            </div>

            {/* Custom Task Input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <input
                type="text"
                value={customTaskPrompt}
                onChange={(e) => setCustomTaskPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLaunchTask(customTaskPrompt);
                  }
                }}
                placeholder="Escribe una instrucción (ej. 'Corregir tipado en ZuneloContext')…"
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  height: 30,
                  padding: '0 9px',
                  borderRadius: 7,
                  border: 'none',
                  background: 'var(--z-fill)',
                  color: 'var(--z-text)',
                  fontSize: 12,
                }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => handleLaunchTask(customTaskPrompt, e)}
                disabled={!customTaskPrompt.trim() || isLaunching}
              >
                {isLaunching ? 'Lanzando…' : 'Lanzar tarea'}
              </Button>
            </div>
          </div>

          {/* Live Activity Log Console */}
          <Accordion title="Terminal y logs en vivo" defaultOpen={true}>
            <div
              style={{
                borderRadius: 8,
                background: '#0d1117',
                border: 'none',
                padding: '10px 12px',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              {/* Default activity */}
              <div style={{ color: '#8b949e' }}>
                <span style={{ color: '#58a6ff' }}>[{agent.elapsed}]</span> Sesión conectada a branch <code>{agent.branch}</code>
              </div>
              <div style={{ color: agent.running ? '#7ee787' : '#d29922' }}>
                <span style={{ color: '#58a6ff' }}>[live]</span> Estado: {agent.running ? 'Corriendo proceso activo en background' : 'En pausa esperando comando'}
              </div>

              {/* Dynamic live logs */}
              {agent.logs?.map((log) => (
                <div
                  key={log.id}
                  style={{
                    color:
                      log.level === 'success'
                        ? '#7ee787'
                        : log.level === 'warn'
                        ? '#d29922'
                        : log.level === 'cmd'
                        ? '#58a6ff'
                        : '#c9d1d9',
                  }}
                >
                  <span style={{ color: '#8b949e' }}>[{log.timestamp}]</span> {log.message}
                </div>
              ))}
            </div>
          </Accordion>
        </div>
      )}

      {/* Footer bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--z-faint)' }}>
          {agent.branch}
        </span>
        <Button
          variant={agent.running ? 'warn' : 'primary'}
          size="sm"
          onClick={handleToggleRunning}
        >
          {agent.running ? 'Pausar agente' : 'Reanudar agente'}
        </Button>
      </div>
    </article>
  );
};

// Also export as AgentsCard for backwards compatibility
export const AgentsCard = AgentCard;
export default AgentCard;
